import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, RotateCcw } from 'lucide-react';

export default function Pagination({
  currentPage,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [5, 10, 20, 50],
  loading = false,
  onReset, // New prop: allows user to reset pagination manually
}) {
  const totalPages = Math.ceil(totalItems / pageSize) || 1;

  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages || loading) return;
    onPageChange(page);
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    onPageSizeChange(newSize);
    // Reset to the first page when page size changes
    onPageChange(1);
  };

  const handleManualReset = () => {
    onPageChange(1);
    if (onReset) onReset();
  };

  /**
   * Generates page numbers with landmarks and ellipsis
   */
  const getPageNumbers = () => {
    const pages = new Set();

    // Always show 1-5
    for (let i = 1; i <= Math.min(5, totalPages); i++) {
      pages.add(i);
    }

    // Always show current page neighbors
    for (let i = Math.max(1, currentPage - 2); i <= Math.min(totalPages, currentPage + 2); i++) {
      pages.add(i);
    }

    // Always show last page
    pages.add(totalPages);

    // Landmarks: multiples of 10, 50, 100 depending on totalPages
    for (let i = 10; i < totalPages; i += 10) {
      let step = 10;
      if (totalPages > 500) step = 50;
      if (totalPages > 2000) step = 100;

      if (i % step === 0) pages.add(i);
    }

    const sortedPages = Array.from(pages).sort((a, b) => a - b);
    const result = [];

    for (let i = 0; i < sortedPages.length; i++) {
      if (i > 0 && sortedPages[i] - sortedPages[i - 1] > 1) {
        result.push('...');
      }
      result.push(sortedPages[i]);
    }

    return result;
  };

  const pages = getPageNumbers();

  // Normalized button sizing (h-8, w-8) and removed aggressive scaling
  const iconBtnClass = `h-8 w-8 rounded-md border transition-colors duration-200 flex items-center justify-center 
     disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-100 
     border-gray-200 text-gray-600 bg-white shadow-sm hover:border-gray-300`;

  const pageBtnClass = (isActive) =>
    `min-w-[32px] h-8 px-2 rounded-md border flex items-center justify-center text-sm font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-100
     ${
       isActive
         ? 'bg-blue-600 text-white border-blue-600 shadow-sm z-10'
         : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-gray-50 shadow-sm'
     } disabled:opacity-50`;

  return (
    <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-6 bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
      {/* Left: Info + Page Size + Reset */}
      <div className="flex items-center gap-3 text-sm text-gray-600 order-2 md:order-1 font-medium flex-wrap justify-center">
        <div className="px-2">
          Showing <span className="text-gray-900 font-bold">{startItem}</span>–
          <span className="text-gray-900 font-bold">{endItem}</span> of{' '}
          <span className="text-gray-900 font-bold">{totalItems}</span>
        </div>

        <div className="flex items-center gap-2 border-l border-gray-200 pl-3">
          <span className="text-gray-500 hidden sm:inline">Rows:</span>

          {/* Fixed Proper Dropdown */}
          <div className="relative flex items-center">
            <select
              className="appearance-none bg-white border border-gray-200 rounded-md pl-3 pr-8 py-1 h-8 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-100 cursor-pointer shadow-sm hover:border-gray-300 text-gray-700"
              value={pageSize}
              onChange={handlePageSizeChange}
              disabled={loading}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          {/* New Manual Reset Prop Button */}
          {onReset && (
            <button
              onClick={handleManualReset}
              disabled={loading}
              title="Reset Pagination"
              className="ml-1 h-8 w-8 flex items-center justify-center text-gray-500 hover:text-blue-600 hover:bg-blue-50 border border-transparent hover:border-blue-100 rounded-md transition-colors"
            >
              <RotateCcw size={15} />
            </button>
          )}
        </div>
      </div>

      {/* Right: Pagination Controls Container with Scrollbar */}
      <div className="flex items-center gap-1.5 order-1 md:order-2 w-full md:w-auto overflow-hidden">
        <div className="flex items-center gap-1 whitespace-nowrap">
          <button
            className={iconBtnClass}
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1 || loading}
            title="First Page"
          >
            <ChevronsLeft size={16} />
          </button>

          <button
            className={iconBtnClass}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1 || loading}
            title="Previous Page"
          >
            <ChevronLeft size={16} />
          </button>
        </div>

        {/* Scrollable Container for Page Buttons */}
        <div className="flex-1 md:flex-initial overflow-x-auto scrollbar-hide py-1 px-1 -mx-1">
          <div className="flex items-center gap-1 min-w-max">
            {pages.map((page, index) =>
              page === '...' ? (
                <span
                  key={`ellipsis-${index}`}
                  className="px-1.5 text-gray-400 font-bold select-none"
                >
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  disabled={loading}
                  className={pageBtnClass(currentPage === page)}
                >
                  {page}
                </button>
              ),
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 whitespace-nowrap">
          <button
            className={iconBtnClass}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || loading}
            title="Next Page"
          >
            <ChevronRight size={16} />
          </button>

          <button
            className={iconBtnClass}
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages || loading}
            title="Last Page"
          >
            <ChevronsRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
